'use client';
import React, { useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Bell, Heart, User, Sun, Moon } from 'lucide-react';
import NavDropdown from '@/components/NavDropdown';
import MobileMenu from '@/components/MobileMenu';
import { useTheme } from '@/contexts/ThemeContext';

const DESKTOP_NAV_ITEMS = [
  { key: 'nav-home', label: 'Home', href: '/' },
  { key: 'nav-hotels', label: 'Hotels', href: '/hotels' },
  { key: 'nav-flights', label: 'Flights', href: '/flights' },
  { key: 'nav-blog', label: 'Blog', href: '/blog' },
  { key: 'nav-payment', label: 'Payment', href: '/payment' },
];

export default function SiteHeader() {
  const router = useRouter();
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('EN');
  const [selectedCurrency, setSelectedCurrency] = useState('DZD');
  const { resolvedTheme, toggleTheme } = useTheme();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname?.startsWith(href);
  };

  return (
    <header
      className="sticky top-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-800"
      style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <button
            onClick={() => router?.push('/')}
            className="flex items-center gap-2 group flex-shrink-0"
            aria-label="Winnbit home">
            <AppLogo size={32} />
            <span
              className="font-display text-lg font-700 text-blue-600 hidden sm:block"
              style={{ fontWeight: 700 }}>
              Winnbit<span className="text-gradient">dz</span>
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
            {DESKTOP_NAV_ITEMS?.map((item) => (
              <button
                key={item?.key}
                onClick={() => router?.push(item?.href)}
                aria-current={isActive(item.href) ? 'page' : undefined}
                className={`px-4 py-2 rounded-xl text-sm font-display font-500 transition-all duration-150 ${
                  isActive(item.href)
                    ? 'text-primary-500 bg-primary-50 dark:bg-primary-900/30 font-600' :'text-gray-600 dark:text-gray-300 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                }`}
                style={{ fontWeight: isActive(item.href) ? 600 : 500 }}>
                {item?.label}
              </button>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            <NavDropdown
              isOpen={langOpen}
              onToggle={() => setLangOpen(!langOpen)}
              selectedLang={selectedLang}
              selectedCurrency={selectedCurrency}
              onSelectLang={(code) => { setSelectedLang(code); setLangOpen(false); }}
              onSelectCurrency={(code) => { setSelectedCurrency(code); setLangOpen(false); }}
            />

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-150"
              aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              {resolvedTheme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <button
              onClick={() => router?.push('/user-dashboard')}
              className="hidden md:flex p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-150"
              aria-label="Notifications">
              <Bell size={20} />
            </button>
            <button
              onClick={() => router?.push('/user-dashboard')}
              className="hidden md:flex p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-150"
              aria-label="Wishlist">
              <Heart size={20} />
            </button>

            <button
              onClick={() => router?.push('/sign-up-login-screen')}
              className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-display font-600 hover:bg-primary-600 active:scale-95 transition-all duration-150 shadow-sm"
              style={{ fontWeight: 600 }}
              aria-label="Sign in to your account">
              <User size={16} />
              Sign In
            </button>

            {/* Mobile: Sign In icon button */}
            <button
              onClick={() => router?.push('/sign-up-login-screen')}
              className="md:hidden p-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 active:scale-95 transition-all duration-150"
              aria-label="Sign in">
              <User size={18} />
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150 min-w-[40px] min-h-[40px] flex items-center justify-center"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && <MobileMenu onClose={() => setMobileOpen(false)} />}
      </div>
    </header>
  );
}